import { useEffect, useState } from "react";
import Table from "../../../../common/Table/Table";
import taService from "../services/ta.service";

const fmt = (d) => {
  if (!d) return "—";
  if (/^\d{4}-\d{2}-\d{2}/.test(d)) {
    const [y, m, day] = d.slice(0, 10).split("-");
    return `${day}-${m}-${y}`;
  }
  return d;
};

const currency = (n) =>
  n === null || n === undefined || n === "" ? "—" : "₹" + Number(n).toLocaleString("en-IN");

const EarlyBadge = () => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 800,
      padding: "2px 7px",
      borderRadius: 12,
      background: "#fef3c7",
      color: "#b45309",
      border: "1px solid #fde68a",
    }}
  >
    Early
  </span>
);

const RENEWAL_COLUMNS = [
  {
    title: "Renewal App ID",
    data_key: "renewalAppId",
    render: (row) => (
      <div>
        <div style={{ fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
          {row.renewalAppId}
          {row.isEarlyRenewal && <EarlyBadge />}
        </div>
        {row.remarks && (
          <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{row.remarks}</div>
        )}
      </div>
    ),
  },
  {
    title: "Expiry",
    data_key: "newExpiryDate",
    render: (row) => (
      <span style={{ whiteSpace: "nowrap" }}>
        <span style={{ color: "#94a3b8" }}>{fmt(row.previousExpiryDate)}</span>
        {" → "}
        <strong>{fmt(row.newExpiryDate)}</strong>
      </span>
    ),
  },
  {
    title: "Previous LOS ID",
    data_key: "previousLosApplicationId",
    render: (row) => row.previousLosApplicationId || "—",
  },
  {
    title: "Limit at renewal",
    data_key: "previousCreditLimit",
    render: (row) => (
      <span style={{ whiteSpace: "nowrap" }}>
        {currency(row.previousCreditLimit)}
        <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>
          {row.previousVehicleLimit ?? "—"} vehicles
        </div>
      </span>
    ),
  },
  {
    title: "Documents",
    data_key: "documents",
    render: (row) =>
      row.documents?.length ? (
        row.documents.map((d) => (
          <a
            key={d.id}
            href={d.documentPath}
            target="_blank"
            rel="noreferrer"
            style={{ display: "block" }}
          >
            {d.fileName}
          </a>
        ))
      ) : (
        <span style={{ color: "#94a3b8" }}>None</span>
      ),
  },
  {
    title: "By / On",
    data_key: "renewedByName",
    render: (row) => (
      <span style={{ whiteSpace: "nowrap" }}>
        {row.renewedByName || "—"}
        <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{row.renewedAt}</div>
      </span>
    ),
  },
];

const AUDIT_COLUMNS = [
  {
    title: "Action",
    data_key: "actionType",
    render: (row) => (
      <span style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
        {String(row.actionType || "").replace(/_/g, " ")}
      </span>
    ),
  },
  {
    title: "Change",
    data_key: "changes",
    render: (row) => {
      const exp = row.changes?.ta_limit_expiry_date;
      return (
        <div>
          {exp && (
            <div>
              Expiry <span style={{ color: "#94a3b8" }}>{fmt(exp.old)}</span>
              {" → "}
              <strong>{fmt(exp.new)}</strong>
            </div>
          )}
          {row.changes?.renewal_app_id && (
            <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>
              App ID {row.changes.renewal_app_id}
              {row.changes?.is_early_renewal ? " · early renewal" : ""}
            </div>
          )}
          {row.notes && (
            <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>{row.notes}</div>
          )}
        </div>
      );
    },
  },
  {
    title: "By",
    data_key: "actorName",
    render: (row) => (
      <span style={{ whiteSpace: "nowrap" }}>
        {row.actorName || "—"}
        {row.actorDesignation ? (
          <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 2 }}>
            {row.actorDesignation}
          </div>
        ) : null}
      </span>
    ),
  },
  { title: "On", data_key: "createdAt", render: (row) => row.createdAt || "—" },
];

/**
 * Renewal cycles and the audit trail behind them, for one onboarding case.
 * Renewals key on the user rather than the case, so a limit renewed before
 * this case existed still shows — that is what "the limit's history" means
 * to whoever is reading the case.
 */
export default function CaseRenewalsPanel({ taOnboardingCaseId }) {
  const [renewals, setRenewals] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!taOnboardingCaseId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await taService.GetCaseRenewals(taOnboardingCaseId);
        if (cancelled) return;
        if (res?.status === 200) {
          setRenewals(res.data?.renewals ?? []);
          setAuditTrail(res.data?.auditTrail ?? []);
        } else {
          setRenewals([]);
          setAuditTrail([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [taOnboardingCaseId]);

  return (
    <div className="mt-3" style={{ paddingBottom: 56 }}>
      <div className="d-flex align-items-center gap-2 mb-2">
        <h6 className="mb-0">Limit Renewals</h6>
        {!loading && (
          <span style={{ fontSize: 12, color: "#64748b" }}>
            {renewals.length === 0
              ? "This limit has not been renewed yet."
              : `${renewals.length} renewal${renewals.length === 1 ? "" : "s"}`}
          </span>
        )}
      </div>

      <Table
        data={renewals}
        columns={RENEWAL_COLUMNS}
        loading={loading}
        loading_rows={2}
        no_data_msg="This limit has not been renewed yet."
      />

      <div className="d-flex align-items-center gap-2 mt-4 mb-2">
        <h6 className="mb-0">Renewal Audit Trail</h6>
        <span style={{ fontSize: 12, color: "#64748b" }}>
          Who renewed this limit, when, and what changed
        </span>
      </div>

      <Table
        data={auditTrail}
        columns={AUDIT_COLUMNS}
        loading={loading}
        loading_rows={2}
        no_data_msg="No renewal activity recorded for this case."
      />

      {/* the last table sat flush against the viewport edge */}
      <div style={{ height: 24 }} />
    </div>
  );
}
